/**
 * What the build optimizer adds on top of `build-data.ts`: the roles a class can fill, the
 * talent-point rules, the per-role stat weights, the request/result schemas crossing the wire
 * to the agent, and the validator both the agent and the API run over a result.
 *
 * Slots, scopes, item sources and `scoreItem` live in `build-data.ts`; import them from there
 * rather than restating them here.
 */
import { z } from 'zod';
import { CLASSES, PROFESSIONS, type PlayerClass } from './classes.js';
import { canEquip } from './character-rules.js';
import {
  BUILD_CONTENTS,
  BUILD_SCOPES,
  BUILD_SCOPE_LABELS,
  BUILD_SLOTS,
  BUILD_SLOT_LABELS,
  sourcesInScope,
  type BuildContent,
  type BuildScope,
  type BuildSlot,
  type ItemSourceView,
  type ItemWeights,
} from './build-data.js';

export const BUILD_ROLES = ['DPS', 'Healer', 'Tank'] as const;
export type BuildRole = (typeof BUILD_ROLES)[number];

/** The roles each class can actually fill; the form offers nothing else. */
export const CLASS_ROLES: Record<PlayerClass, BuildRole[]> = {
  Warrior: ['DPS', 'Tank'],
  Paladin: ['DPS', 'Healer', 'Tank'],
  Hunter: ['DPS'],
  Rogue: ['DPS'],
  Priest: ['DPS', 'Healer'],
  Shaman: ['DPS', 'Healer'],
  Mage: ['DPS'],
  Warlock: ['DPS'],
  Druid: ['DPS', 'Healer', 'Tank'],
};

/**
 * Talent points available at a level: one per level from 10, so 51 at the level cap. Forever
 * keeps Classic's grant (see the plan's Assumptions); this is the single place to change it.
 */
export function talentPointsAt(level: number): number {
  return Math.max(0, level - 9);
}

/** Points a tree must already hold before a talent of this tier (1-7) can be taken. */
export function tierPointsRequired(tier: number): number {
  return 5 * (tier - 1);
}

/**
 * The stat weights the prompt suggests per role and the fake agent ranks with. Keys are the
 * client's stat names plus `armor` and `dps`, as `scoreItem` expects.
 */
export const ROLE_DEFAULT_WEIGHTS: Record<BuildRole, ItemWeights> = {
  Tank: { Stamina: 1, armor: 0.1, Defense: 2, Dodge: 1.5, Parry: 1.5, Block: 1, Strength: 0.5, Agility: 0.5 },
  Healer: { Healing: 1, 'Spell Power': 0.9, Intellect: 0.6, Spirit: 0.5, 'Mana Regeneration': 2, Stamina: 0.2 },
  DPS: {
    'Attack Power': 0.5,
    Strength: 1,
    Agility: 1,
    'Critical Strike': 3,
    Hit: 4,
    'Spell Damage': 1,
    'Spell Power': 0.9,
    Intellect: 0.4,
    Stamina: 0.2,
    dps: 2,
  },
};

/**
 * Item subclasses that occupy both hands. A candidate row carries the *build* slot it was
 * queried for rather than the item's own `Two-Hand` slot name, so this is how either agent
 * recognises a two-hander before it also fills the off hand.
 */
export const TWO_HANDED_SUBCLASSES: ReadonlySet<string> = new Set([
  'Two-Handed Sword',
  'Two-Handed Mace',
  'Two-Handed Axe',
  'Staff',
  'Polearm',
  'Spears',
]);

export function isTwoHanded(subclass: string | undefined): boolean {
  return !!subclass && TWO_HANDED_SUBCLASSES.has(subclass);
}

export const BUILD_STATUSES = ['queued', 'generating', 'ready', 'failed'] as const;
export type BuildStatus = (typeof BUILD_STATUSES)[number];

/** A request with no character and no equipped items may reuse a build this recent. */
export const BUILD_REUSE_DAYS = 7;

/** Agent invocations in flight at once; the rest stay `queued`. */
export const BUILD_MAX_CONCURRENT = 2;
/** Per-invocation ceiling, enforced by the runner rather than the invoker. */
export const BUILD_TIMEOUT_MS = 600_000;
/** Builds older than this still queued or generating at boot were killed by a restart. */
export const BUILD_STALE_MS = 15 * 60 * 1000;
/** Model calls one build may make before the agent gives up. */
export const BUILD_MAX_MODEL_CALLS = 20;

const uuid = z.uuid();
const note = z.string().max(300);

/** Items the agent may offer per slot beyond its pick, so a slot's dropdown never shows more than 5. */
export const BUILD_GEAR_OPTIONS_MAX = 4;

/** `POST /api/builds`. With `characterId` the snapshot supplies class, race, faction and gear. */
export const buildRequestSchema = z.object({
  characterId: uuid.optional(),
  class: z.enum(CLASSES),
  race: z.string().min(1).max(40),
  faction: z.enum(['Alliance', 'Horde']),
  level: z.number().int().min(1).max(60),
  role: z.enum(BUILD_ROLES),
  /** A talent tree that fits the class and role (`specsFor`); left out, the first such tree. */
  spec: z.string().min(1).max(40).optional(),
  content: z.enum(BUILD_CONTENTS).default('pve'),
  scope: z.enum(BUILD_SCOPES),
  equipped: z.array(z.object({ artifactId: uuid })).max(BUILD_SLOTS.length).default([]),
});
export type BuildRequest = z.infer<typeof buildRequestSchema>;

/** What the agent must return. Every id is checked against the run's tool results separately. */
export const buildResultSchema = z.object({
  statPriority: z.array(z.string().min(1).max(40)).min(1).max(8),
  talents: z.object({
    allocation: z.array(z.object({ abilityId: uuid, points: z.number().int().min(1) })),
    order: z.array(
      z.object({
        fromLevel: z.number().int().min(10).max(60),
        toLevel: z.number().int().min(10).max(60),
        abilityId: uuid,
        points: z.number().int().min(1),
      }),
    ),
    summary: z.string().max(2000),
  }),
  gear: z.array(
    z.object({
      slot: z.enum(BUILD_SLOTS),
      item: z.object({ artifactId: uuid }),
      enchant: z.object({ id: z.number().int().min(0), reason: z.string().max(200).optional() }).optional(),
      /** Kept for builds made before `options`; new builds list the easier item among the options. */
      alternative: z.object({ artifactId: uuid, reason: note }).optional(),
      /** Other items the agent would also recommend here, best first, each with why. */
      options: z.array(z.object({ artifactId: uuid, reason: note })).max(BUILD_GEAR_OPTIONS_MAX).optional(),
      upgrade: z.boolean().optional(),
      note: note.optional(),
    }),
  ),
  consumables: z.array(z.object({ artifactId: uuid, when: z.string().max(200) })).max(12),
  worldBuffs: z.array(z.object({ spellId: z.number().int().min(0), why: z.string().max(200) })).max(6),
  professions: z
    .array(z.object({ name: z.enum(PROFESSIONS), why: note, recipes: z.array(z.object({ artifactId: uuid })).max(6).optional() }))
    .max(2),
  reasoning: z.string().max(4000),
  gaps: z.array(note).max(10),
});
export type BuildResult = z.infer<typeof buildResultSchema>;

const usageSchema = z.object({
  model: z.string().max(100),
  calls: z.number().int().min(0),
  inputTokens: z.number().int().min(0),
  outputTokens: z.number().int().min(0),
  cacheReadTokens: z.number().int().min(0),
  cacheWriteTokens: z.number().int().min(0),
  durationMs: z.number().int().min(0).optional(),
});
export type BuildUsage = z.infer<typeof usageSchema>;

/** The body the API posts to the agent runtime. */
export const buildAgentRequestSchema = z.object({
  version: z.literal(1),
  buildId: uuid,
  apiBaseUrl: z.string().min(1),
  dataBuild: z.string().max(40),
  scope: z.enum(BUILD_SCOPES),
  character: z.object({
    class: z.enum(CLASSES),
    race: z.string().min(1).max(40),
    faction: z.enum(['Alliance', 'Horde']),
    level: z.number().int().min(1).max(60),
    role: z.enum(BUILD_ROLES),
    // Optional on the wire so an API and agent deployed a moment apart still agree: missing means
    // the role's first tree, for PvE.
    spec: z.string().min(1).max(40).optional(),
    content: z.enum(BUILD_CONTENTS).default('pve'),
    equipped: z.array(
      z.object({
        artifactId: uuid.optional(),
        name: z.string().max(200),
        slot: z.enum(BUILD_SLOTS),
        itemLevel: z.number().int().min(0).optional(),
        enchantId: z.number().int().min(0).optional(),
      }),
    ),
    knownTalents: z.array(z.object({ artifactId: uuid.optional(), name: z.string().max(200), rank: z.number().int().min(0) })).optional(),
  }),
});
export type BuildAgentRequest = z.infer<typeof buildAgentRequestSchema>;

/** An `ok: false` answer is a valid response, and it still carries the tokens it spent. */
export const buildAgentResponseSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), build: buildResultSchema, usage: usageSchema }),
  z.object({ ok: z.literal(false), error: z.string().max(2000), usage: usageSchema.optional() }),
]);
export type BuildAgentResponse = z.infer<typeof buildAgentResponseSchema>;

/** `site_settings` key `build`. Prices default to the first-party Claude Opus 5 rates. */
export const buildSettingsSchema = z.object({
  enabled: z.boolean().default(true),
  dailyAllowance: z.number().int().min(0).max(100).default(5),
  inputUsdPerMTok: z.number().min(0).default(5),
  outputUsdPerMTok: z.number().min(0).default(25),
});
export type BuildSettings = z.infer<typeof buildSettingsSchema>;

// ---- validation ------------------------------------------------------------------------

export interface BuildValidationTalent {
  tree: string;
  tier: number;
  maxRank: number;
  prerequisite?: { artifactId: string; rank: number };
}

export interface BuildValidationItem {
  slots: BuildSlot[];
  twoHand: boolean;
  subclass?: string;
  /** From `ItemSparse.AllowableClass`; absent means every class may use it. */
  classes?: string[];
  sources: ItemSourceView[];
}

/**
 * Everything the validator needs, gathered by whoever calls it: the agent builds it from the
 * rows its tools returned, the API rebuilds it from the database. The validator does no I/O,
 * so both sides reach the same verdict.
 */
export interface BuildValidationContext {
  level: number;
  class: string;
  race: string;
  scope: BuildScope;
  /** Left out, `pve`: PvP rewards follow the scope's own rule. */
  content?: BuildContent;
  talents: Map<string, BuildValidationTalent>;
  items: Map<string, BuildValidationItem>;
  enchants: Map<number, { slots: BuildSlot[] }>;
  consumables: Set<string>;
  worldBuffs: Set<number>;
  /** Recipe artifact id -> the profession that teaches it. */
  recipes: Map<string, string>;
  equippedIds: Set<string>;
}

function checkTalents(result: BuildResult, ctx: BuildValidationContext, errors: string[]): void {
  const { allocation, order } = result.talents;
  const budget = talentPointsAt(ctx.level);
  const spent = allocation.reduce((sum, a) => sum + a.points, 0);
  if (spent !== budget) {
    errors.push(spent < budget ? `${budget - spent} of ${budget} talent points are unallocated` : `${spent} talent points allocated but only ${budget} are available at level ${ctx.level}`);
  }

  const pointsByTree = new Map<string, number>();
  const pointsById = new Map<string, number>();
  for (const a of allocation) {
    const talent = ctx.talents.get(a.abilityId);
    if (!talent) {
      errors.push(`Talent ${a.abilityId} is unknown or was not shown by a data tool`);
      continue;
    }
    if (a.points > talent.maxRank) errors.push(`A talent with ${talent.maxRank} ranks cannot take ${a.points} points`);
    pointsById.set(a.abilityId, (pointsById.get(a.abilityId) ?? 0) + a.points);
    pointsByTree.set(talent.tree, (pointsByTree.get(talent.tree) ?? 0) + a.points);
  }

  for (const a of allocation) {
    const talent = ctx.talents.get(a.abilityId);
    if (!talent) continue;
    const required = tierPointsRequired(talent.tier);
    if (required > 0) {
      let below = 0;
      for (const other of allocation) {
        const o = ctx.talents.get(other.abilityId);
        if (o && o.tree === talent.tree && o.tier < talent.tier) below += other.points;
      }
      if (below < required) errors.push(`A tier ${talent.tier} talent needs ${required} points in ${talent.tree} first, but only ${below} are spent below it`);
    }
    if (talent.prerequisite) {
      const held = pointsById.get(talent.prerequisite.artifactId) ?? 0;
      if (held < talent.prerequisite.rank) {
        errors.push(`A talent needs its prerequisite at rank ${talent.prerequisite.rank}, but it holds ${held}`);
      }
    }
  }

  if (budget === 0) {
    if (order.length > 0) errors.push(`No talent points are available at level ${ctx.level}, so the levelling order must be empty`);
    return;
  }

  const orderByTalent = new Map<string, number>();
  const covered = new Map<number, number>();
  for (const step of order) {
    if (step.toLevel < step.fromLevel) {
      errors.push(`A levelling order step runs from level ${step.fromLevel} back to ${step.toLevel}`);
      continue;
    }
    const span = step.toLevel - step.fromLevel + 1;
    if (span !== step.points) errors.push(`Levels ${step.fromLevel} to ${step.toLevel} cover ${span} levels but place ${step.points} points`);
    for (let l = step.fromLevel; l <= step.toLevel; l += 1) covered.set(l, (covered.get(l) ?? 0) + 1);
    orderByTalent.set(step.abilityId, (orderByTalent.get(step.abilityId) ?? 0) + step.points);
  }
  for (let l = 10; l <= ctx.level; l += 1) {
    const times = covered.get(l) ?? 0;
    if (times === 0) errors.push(`The levelling order does not say what to take at level ${l}`);
    else if (times > 1) errors.push(`The levelling order places ${times} talents at level ${l}`);
  }
  for (const l of covered.keys()) {
    if (l < 10 || l > ctx.level) errors.push(`The levelling order covers level ${l}, outside 10 to ${ctx.level}`);
  }
  for (const [id, points] of orderByTalent) {
    const allocated = pointsById.get(id) ?? 0;
    if (points !== allocated) errors.push(`The levelling order places ${points} points in a talent the allocation gives ${allocated}`);
  }
  for (const [id, points] of pointsById) {
    if (!orderByTalent.has(id)) errors.push(`The levelling order never places the ${points} points the allocation gives a talent`);
  }
}

function checkGear(result: BuildResult, ctx: BuildValidationContext, errors: string[]): void {
  const usedSlots = new Set<BuildSlot>();
  const byId = new Map<string, BuildSlot[]>();
  let twoHandedMainHand = false;
  let hasOffHand = false;

  for (const row of result.gear) {
    if (usedSlots.has(row.slot)) errors.push(`The ${BUILD_SLOT_LABELS[row.slot]} slot is filled twice`);
    usedSlots.add(row.slot);

    const item = ctx.items.get(row.item.artifactId);
    if (!item) {
      errors.push(`Item ${row.item.artifactId} is unknown or was not shown by a data tool`);
      continue;
    }
    if (!item.slots.includes(row.slot)) errors.push(`That item does not go in the ${BUILD_SLOT_LABELS[row.slot]} slot`);
    if (!canEquip(ctx.class, ctx.race, item.subclass, ctx.level)) {
      errors.push(`A level ${ctx.level} ${ctx.race} ${ctx.class} cannot equip ${item.subclass ?? 'that item'}`);
    }
    if (item.classes && !item.classes.includes(ctx.class)) errors.push(`That ${BUILD_SLOT_LABELS[row.slot]} item cannot be equipped by a ${ctx.class}`);
    if (!sourcesInScope(item.sources, ctx.scope, ctx.content)) errors.push(`That ${BUILD_SLOT_LABELS[row.slot]} item is out of scope for ${BUILD_SCOPE_LABELS[ctx.scope]}`);

    byId.set(row.item.artifactId, [...(byId.get(row.item.artifactId) ?? []), row.slot]);
    if (row.slot === 'mainHand' && item.twoHand) twoHandedMainHand = true;
    if (row.slot === 'offHand') hasOffHand = true;

    if (row.enchant) {
      const enchant = ctx.enchants.get(row.enchant.id);
      if (!enchant) errors.push(`Enchant ${row.enchant.id} is unknown or was not shown by a data tool`);
      else if (!enchant.slots.includes(row.slot)) errors.push(`Enchant ${row.enchant.id} does not fit the ${BUILD_SLOT_LABELS[row.slot]} slot`);
    }
    const offered = new Set<string>();
    for (const option of row.options ?? []) {
      const label = `An option for ${BUILD_SLOT_LABELS[row.slot]}`;
      if (option.artifactId === row.item.artifactId) errors.push(`${label} repeats the pick itself`);
      else if (offered.has(option.artifactId)) errors.push(`${label} is listed twice`);
      offered.add(option.artifactId);
      const alt = ctx.items.get(option.artifactId);
      if (!alt) {
        errors.push(`${label} (${option.artifactId}) is unknown or was not shown by a data tool`);
        continue;
      }
      if (!alt.slots.includes(row.slot)) errors.push(`${label} does not go in that slot`);
      if (!canEquip(ctx.class, ctx.race, alt.subclass, ctx.level)) errors.push(`${label} is ${alt.subclass ?? 'an item'} a level ${ctx.level} ${ctx.race} ${ctx.class} cannot equip`);
      if (alt.classes && !alt.classes.includes(ctx.class)) errors.push(`${label} cannot be equipped by a ${ctx.class}`);
      if (!sourcesInScope(alt.sources, ctx.scope, ctx.content)) errors.push(`${label} is out of scope for ${BUILD_SCOPE_LABELS[ctx.scope]}`);
    }
    if (row.alternative) {
      const alt = ctx.items.get(row.alternative.artifactId);
      if (!alt) errors.push(`Alternative item ${row.alternative.artifactId} is unknown or was not shown by a data tool`);
      else if (!alt.slots.includes(row.slot)) errors.push(`The alternative for ${BUILD_SLOT_LABELS[row.slot]} does not go in that slot`);
    }
  }

  if (twoHandedMainHand && hasOffHand) errors.push('A two-hand weapon leaves no room for an off hand item');
  for (const [id, slots] of byId) {
    if (slots.length > 1) errors.push(`Item ${id} is worn twice, in ${slots.map((s) => BUILD_SLOT_LABELS[s]).join(' and ')}`);
  }
}

/**
 * Check a result against the character and the exact rows a run was shown. An empty array
 * means the build is legal; anything else is a human-readable reason it is not.
 */
export function validateBuildResult(result: BuildResult, ctx: BuildValidationContext): string[] {
  const errors: string[] = [];
  checkTalents(result, ctx, errors);
  checkGear(result, ctx, errors);

  for (const c of result.consumables) {
    if (!ctx.consumables.has(c.artifactId)) errors.push(`Consumable ${c.artifactId} is unknown or was not shown by a data tool`);
  }
  for (const b of result.worldBuffs) {
    if (!ctx.worldBuffs.has(b.spellId)) errors.push(`World buff ${b.spellId} is unknown or was not shown by a data tool`);
  }
  for (const p of result.professions) {
    for (const r of p.recipes ?? []) {
      const taughtBy = ctx.recipes.get(r.artifactId);
      if (!taughtBy) errors.push(`Recipe ${r.artifactId} is unknown or was not shown by a data tool`);
      else if (taughtBy !== p.name) errors.push(`Recipe ${r.artifactId} belongs to ${taughtBy}, not ${p.name}`);
    }
  }
  return errors;
}
